import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Github, Check, ArrowRight, RefreshCw, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { getGithubSetup, continueToProjects, skipOnboarding } from '@/api/onboarding';

export function SetupGithubPage() {
  const [githubConnected, setGithubConnected] = useState(false);
  const [installUrl, setInstallUrl] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isContinuing, setIsContinuing] = useState(false);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    loadGithubStatus();
    
    // Handle GitHub connection success
    if (searchParams.get('github_connected') === 'true') {
      toast.success('GitHub account connected!', {
        description: 'Great! Now you can create your first project.',
      });
      // Remove the query parameter - use new URLSearchParams to avoid mutation
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('github_connected');
      setSearchParams(newParams);
      // Reload status to reflect the connection
      loadGithubStatus();
    }
  }, [searchParams, setSearchParams]);

  const loadGithubStatus = async () => {
    try {
      const response = await getGithubSetup();
      setGithubConnected(response.github_connected);
      setInstallUrl(response.install_url);
    } catch (error) {
      toast.error('Failed to load GitHub setup status');
    } finally {
      setIsLoading(false);
    }
  };

  const handleConnectGithub = () => {
    if (installUrl) {
      window.location.href = installUrl;
    } else {
      // Fallback to the known endpoint
      window.location.href = '/api/v1/github_app/install';
    }
  };

  const handleContinueToProjects = async () => {
    setIsContinuing(true);
    try {
      const response = await continueToProjects();
      if (response.success && response.redirect) {
        navigate(response.redirect);
      } else if (response.success) {
        navigate('/projects');
      } else {
        toast.error('Failed to continue to projects');
      }
    } catch (error) {
      toast.error('Failed to continue to projects');
    } finally {
      setIsContinuing(false);
    }
  };

  const handleSkip = async () => {
    setIsLoading(true);
    try {
      const response = await skipOnboarding();
      if (response.success && response.redirect) {
        navigate(response.redirect);
      } else if (response.success) {
        navigate('/projects');
      } else {
        toast.error(response.error || 'Failed to skip onboarding');
      }
    } catch (error) {
      toast.error('Failed to skip onboarding. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="flex items-center gap-3">
          <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
          <span className="text-gray-600">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-12 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Connect Your GitHub Account
          </h1>
          <p className="text-lg text-gray-600">
            We'll need access to your GitHub repositories to deploy your Odoo projects
          </p>
        </div>

        {/* Step Indicator */}
        <div className="flex items-center justify-center gap-4 mb-12">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-semibold">
              1
            </div>
            <span className="text-sm font-medium text-gray-900">Setup</span>
          </div>
          <div className="w-16 h-0.5 bg-gray-300"></div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-semibold">
              2
            </div>
            <span className="text-sm font-medium text-gray-900">Create Project</span>
          </div>
        </div>

        {/* Main Card */}
        <Card className="shadow-lg">
          <CardContent className="p-8">
            {githubConnected ? (
              /* GitHub Connected State */
              <>
                <div className="flex items-center justify-center w-20 h-20 bg-green-100 rounded-2xl mb-6 mx-auto">
                  <Check className="w-10 h-10 text-green-600" />
                </div>

                <h2 className="text-2xl font-semibold text-center mb-4">
                  ✅ GitHub Connected!
                </h2>

                <Alert className="mb-6">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Great! Your GitHub account is already connected. You're ready to create your first project.
                  </AlertDescription>
                </Alert>

                <div className="space-y-4 mb-8">
                  <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                    <div className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0">
                      ✓
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">GitHub Connected</h3>
                      <p className="text-sm text-gray-600">We can access your repositories</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                    <div className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0">
                      ✓
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">Ready for Deployment</h3>
                      <p className="text-sm text-gray-600">You can now create projects and deploy</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 p-4 bg-blue-50 rounded-lg">
                    <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0">
                      3
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">Create Your First Project</h3>
                      <p className="text-sm text-gray-600">Let's set up your Odoo instance</p>
                    </div>
                  </div>
                </div>

                <div className="text-center">
                  <Button
                    onClick={handleContinueToProjects}
                    disabled={isContinuing}
                    className="px-8 py-3 text-lg"
                  >
                    {isContinuing ? (
                      <>
                        <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                        Setting up...
                      </>
                    ) : (
                      <>
                        Continue to Create Project
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </>
                    )}
                  </Button>
                </div>
              </>
            ) : (
              /* GitHub Not Connected State */
              <>
                <div className="flex items-center justify-center w-20 h-20 bg-gray-900 rounded-2xl mb-6 mx-auto">
                  <Github className="w-10 h-10 text-white" />
                </div>

                <h2 className="text-2xl font-semibold text-center mb-4">
                  Connect Your GitHub Account
                </h2>

                <div className="space-y-4 mb-8">
                  <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                    <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0">
                      1
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">Access Your Repositories</h3>
                      <p className="text-sm text-gray-600">We'll fetch your Odoo repositories so you can deploy them</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                    <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0">
                      2
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">Automated Deployments</h3>
                      <p className="text-sm text-gray-600">Set up CI/CD pipelines that deploy when you push code</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                    <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0">
                      3
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">Branch Management</h3>
                      <p className="text-sm text-gray-600">Each branch automatically deploys to its own environment</p>
                    </div>
                  </div>
                </div>

                <div className="text-center space-y-4">
                  <Button
                    onClick={handleConnectGithub}
                    className="px-8 py-3 text-lg bg-gray-900 hover:bg-gray-800"
                  >
                    <Github className="w-5 h-5 mr-2" />
                    Connect GitHub Account
                  </Button>

                  <p className="text-sm text-gray-500">
                    You'll be redirected to GitHub to authorize the doploy app
                  </p>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Skip Link */}
        <div className="text-center mt-6">
          <Button
            variant="ghost"
            onClick={handleSkip}
            className="text-gray-500 hover:text-gray-700"
          >
            Skip for now
          </Button>
        </div>
      </div>
    </div>
  );
}
